import { useFormik } from "formik";
import React, { useCallback, useEffect } from "react";
import clsx from "clsx";
import store from "app/redux/store/store";
import { gravarTelasAlteradasNaoSalvaAction } from "app/redux/telaOrcamentoAlteradaNaoSalvaSlice";
import { InputTelefoneCelular } from "../../inputs/InputTelefoneCelular";

const estilosInputs = require("../../../estilos/inputs.module.scss").default;

export type ComunicacaoItemContract = {
  Rtipo: number;
  Oid: number;
  Valor: string;
  Alterado: boolean;
};

export type DadoAdicionalItemContract = {
  RDefinicao: number;
  Oid: number;
  Valor: string;
  Alterado: boolean;
};

export type ContatoContract = {
  Oid: number;
  Nome: string;
  Cargo: string;
  Alterado: boolean;
};

export type DadosGuiaClienteContract = {
  Comunicacoes: ComunicacaoItemContract[];
  DadosAdicionais: DadoAdicionalItemContract[];
  Contato: ContatoContract;
};

export type FormularioDadosGuiaCliente = {
  Tel1: string;
  Tel2: string;
  Fax: string;
  Mail1: string;
  Mail2: string;
  Mail3: string;
  Site: string;
  Celular: string;
  Banco: string;
  Agencia: string;
  Conta: string;
  Crea: string;
  ContatoNome: string;
  ContatoCargo: string;
};

export const formularioVazio: FormularioDadosGuiaCliente = {
  Tel1: "",
  Tel2: "",
  Fax: "",
  Mail1: "",
  Mail2: "",
  Mail3: "",
  Site: "",
  Celular: "",
  Banco: "",
  Agencia: "",
  Conta: "",
  Crea: "",
  ContatoNome: "",
  ContatoCargo: "",
};

export type DadosClienteProps = {
  somenteLeitura: boolean;
  permitirAlterarDadosBasicos: boolean;
  dados: FormularioDadosGuiaCliente;
  manterDadosTemporarios: (dados: FormularioDadosGuiaCliente) => void;
};

export const DadosCliente = (props: DadosClienteProps) => {
  const formik = useFormik<FormularioDadosGuiaCliente>({
    initialValues: { ...formularioVazio, ...props.dados },
    onSubmit: () => {},
    enableReinitialize: true,
  });

  useEffect(() => {
    props.manterDadosTemporarios(formik.values);
  }, [formik.values]);

  const desabilitado = props.somenteLeitura || !props.permitirAlterarDadosBasicos;

  const dispararAlteracao = useCallback(() => {
    store.dispatch(gravarTelasAlteradasNaoSalvaAction("clientePesquisa_dadosCliente"));
  }, []);

  const handleChange = useCallback(
    (event) => {
      formik.handleChange(event);
      dispararAlteracao();
    },
    [formik, dispararAlteracao]
  );

  // 490808 - label primária (primeiro campo da linha) alinha a coluna esquerda em 160px à direita;
  // labels secundárias (2º/3º campo da mesma linha) ficam com largura natural.
  const classeLabel = (primeiro: boolean) =>
    clsx(
      "_font-normal _mb-1 _pr-[10px]",
      primeiro ? "_w-[160px] _text-right" : "_pl-[10px]"
    );

  const campoTelefone = (
    nome: keyof FormularioDadosGuiaCliente,
    id: string,
    label: string,
    primeiro: boolean = true
  ) => (
    <div className="_flex _items-center">
      <label className={classeLabel(primeiro)}>{label}</label>
      <InputTelefoneCelular
        id={id}
        disabled={desabilitado}
        placeholder="(99) 99999-9999"
        telefoneCelular={formik.values[nome]}
        onTelefoneCelularModificado={(valor) => {
          formik.setFieldValue(nome, valor);
          dispararAlteracao();
        }}
      />
    </div>
  );

  const campoTexto = (
    nome: keyof FormularioDadosGuiaCliente,
    id: string,
    label: string,
    primeiro: boolean = true,
    classeInput?: string,
    maxLength?: number
  ) => (
    <div className="_flex _items-center">
      <label className={classeLabel(primeiro)}>{label}</label>
      <input
        className={clsx(estilosInputs.inputPadrao, classeInput)}
        type="text"
        name={nome}
        id={id}
        maxLength={maxLength}
        value={formik.values[nome] ?? ""}
        disabled={desabilitado}
        onChange={handleChange}
      />
    </div>
  );

  return (
    <div className="_space-y-1 _mt-8">
      {campoTelefone("Tel1", "DadosCliente_CampoTel1", "Telefone 1")}
      {campoTelefone("Tel2", "DadosCliente_CampoTel2", "Telefone 2")}
      {campoTelefone("Fax", "DadosCliente_CampoFax", "Fax")}
      {campoTelefone("Celular", "DadosCliente_CampoCelular", "Celular")}

      {campoTexto("Mail1", "DadosCliente_CampoMail1", "E-mail 1")}
      {campoTexto("Mail2", "DadosCliente_CampoMail2", "E-mail 2")}
      {campoTexto("Mail3", "DadosCliente_CampoMail3", "E-mail 3")}

      {campoTexto("Site", "DadosCliente_CampoSite", "Site")}

      <div className="_flex _flex-wrap _items-center">
        {campoTexto("Banco", "DadosCliente_CampoBanco", "Banco", true, "_w-[110px]")}
        {campoTexto("Agencia", "DadosCliente_CampoAgencia", "Agência", false, "_w-[80px]")}
        {campoTexto("Conta", "DadosCliente_CampoConta", "Conta", false, "_w-[110px]")}
      </div>

      {campoTexto("Crea", "DadosCliente_CampoCrea", "CREA")}

      <div className="_flex _flex-wrap _items-center">
        {campoTexto("ContatoNome", "DadosCliente_CampoContatoNome", "Nome Contato")}
        {campoTexto("ContatoCargo", "DadosCliente_CampoContatoCargo", "Cargo Contato", false)}
      </div>
    </div>
  );
};
